import { isEmpty } from 'lodash';
import { useState } from 'react';
import type { IOorkV1DiagnosticReportCustomControlData } from '@common/interfaces/ophthalmology/OorkAndOcrk/oorkV1DiagnosticReportCustomControlData';
import type { IOorkV1ProcedureCustomControlData } from '@common/interfaces/ophthalmology/OorkAndOcrk/oorkV1ProcedureCustomControlData';
import { ProcessEntity, saveTypeEnum, ToastType } from '@common/enums';
import { ProceedWithoutFiles } from '@frontend/components/elements/ProceedWithoutFiles';
import { ReloadEpisode } from '@frontend/components/pages/episodes/reloadEpisode';
import { translateKey } from '@frontend/translate';
import { showServiceToast, showToast } from '@frontend/utils/index';
import type { IDiagnosticReportAddEdit } from '../../../diagnosticReports/DiagnosticReportItemAddEdit';
import type { IProcedureAddEdit } from '../../../procedures/ProcedureItemAddEdit';
import type { ICustomControlProcedureDiagnosticReportAddEditProps } from '../../interfaces';
import { OcrkV1RequestAddEdit } from '../../OCRK/v1/OcrkV1RequestAddEdit';
import { OorkV1DiagnosticReportAddEdit } from './OorkV1DiagnosticReportAddEdit';
import { OorkV1ProcedureAddEdit } from './OorkV1ProcedureAddEdit';

interface IDiagnosticReportOork extends IDiagnosticReportAddEdit {
    customControlData: IOorkV1DiagnosticReportCustomControlData;
}

interface IProcedureOork extends IProcedureAddEdit {
    customControlData: IOorkV1ProcedureCustomControlData;
}

export interface IOorkV1ProcedureDiagnosticReportAddEditProps
    extends ICustomControlProcedureDiagnosticReportAddEditProps {
    diagnosticReport: IDiagnosticReportOork;
    procedure: IProcedureOork;
}

export function OorkV1ProcedureDiagnosticReportAddEdit(props: IOorkV1ProcedureDiagnosticReportAddEditProps) {
    const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);
    const [showProceedWithoutFiles, setShowProceedWithoutFiles] = useState(false);

    return (
        <>
            <OorkV1ProcedureAddEdit
                isReadOnly={false}
                {...props}
            />
            <OorkV1DiagnosticReportAddEdit {...props} />
            <OcrkV1RequestAddEdit
                isSaveButtonDisabled={isSaveButtonDisabled}
                saveProcedureDiagnosticReport={async (value) => await onSaveProcedureDiagnosticReport(value)}
                {...props}
            />

            {showProceedWithoutFiles && (
                <ProceedWithoutFiles
                    entity={ProcessEntity.procedure}
                    onNo={async () => {
                        setShowProceedWithoutFiles(false);
                        setIsSaveButtonDisabled(false);
                    }}
                    onYes={async () => await saveProcedureDiagnosticReport(saveTypeEnum.final)}
                    serviceType={props.service.serviceType}
                />
            )}
        </>
    );

    async function onSaveProcedureDiagnosticReport(saveType: saveTypeEnum): Promise<void> {
        setIsSaveButtonDisabled(true);

        const validatedProcedure = await props.validateProcedure(saveType);
        if (!validatedProcedure) {
            return setIsSaveButtonDisabled(false);
        }

        if (saveType === saveTypeEnum.final && !hasFiles(props.procedure)) {
            return setShowProceedWithoutFiles(true);
        }

        return saveProcedureDiagnosticReport(saveType);
    }

    async function saveProcedureDiagnosticReport(saveType: saveTypeEnum): Promise<void> {
        const requestId = await props.onSaveProcedure(saveType);
        setIsSaveButtonDisabled(false);
        if (!requestId) {
            return;
        }

        // Will handle state changes and reloading
        await props.onSave(requestId);

        if (saveType === saveTypeEnum.final) {
            showToast(ToastType.success, translateKey('toast.submitExaminationToOphthalmologist'));
            // Reload episode to render the consult request
            await props.reloadProcess(ReloadEpisode.reloadAll);
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'toast',
                translateKey('toast.draftDefaultMessage')
            );
        }
    }

    function hasFiles(procedure: IProcedureOork) {
        return !isEmpty(procedure?.assets) || !isEmpty(procedure?.customControlData?.files);
    }
}
